import {AxiosError} from "axios";
import {ApiConfig} from "components/src/api/portal/ApiConfig";
import {AuthApi, PortalSignupValidationResult} from "components/src/api/portal/generated";
import {LocaleSelector} from "components/src/common/LocaleSelector";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {useEffect, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';
import {useNavigate, useSearchParams} from "react-router-dom";

/*
 * REST api for accessing the back-end
 */
const authApi = new AuthApi(ApiConfig.config());

export const PortalSignup = () =>
{
    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();

    /**
     * The search parameters of the URL. The d parameter contains the signup validation code.
     */
    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded signup validation code
     */
    const [signupCode, setSignupCode] = useState<string>();
    /**
     * The result of the validation code
     */
    const [signupValidation, setSignupValidation] = useState<PortalSignupValidationResult>({email: "", securityQuestionsEnabled: false});
    /**
     * The security question for the user. Only set if security questions are enabled.
     */
    const [securityQuestion, setSecurityQuestion] = useState<string>();
    /**
     * The security answer for the user. Only set if security questions are enabled.
     */
    const [securityAnswer, setSecurityAnswer] = useState<string>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The password for the user
     */
    const [password, setPassword] = useState<string>();
    /**
     * The repeated password for the user
     */
    const [passwordRepeat, setPasswordRepeat] = useState<string>();
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const staticSecurityQuestion = t(['portal.signup.security-question-static'])

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
    }

    useEffect(() =>
    {
        const dParameter = searchParams.get("d");

        if (dParameter === null)
        {
            logger.warn("signup URL is missing the d parameter");

            setErrorMessage(t('portal.signup.data-missing'));

            return;
        }

        authApi.authControllerValidatePortalSignup(dParameter)
            .then(response =>
            {
                setSignupCode(dParameter);

                setSignupValidation(response.data);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating signup link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
            })
    }, [searchParams, t]);

    const signup = () =>
    {
        if (signupCode === undefined) {
            throw new Error("signupCode is undefined");
        }

        if (password === undefined) {
            throw new Error("password is undefined");
        }

        if (password !== passwordRepeat)
        {
            setErrorMessage(t('portal.signup.password-mismatch'));
            return;
        }

        authApi.authControllerSetPortalSignupPassword(
                    signupCode,
                    {password: password, questionAndAnswers: [{question: activeSecurityQuestion(), answer: securityAnswer}]}
        )
            .then(() =>
            {
                navigate("/");
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating signup link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    const renderSecurityQuestion = () =>
    {
        if (!signupValidation.securityQuestionsEnabled) {
            return undefined;
        }

        return (
            <>
                <p className="h4">{t('portal.signup.security-question-header')}</p>

                <p>
                    {t(['portal.signup.security-question-text'])}
                </p>

                <FloatingLabel label={t('portal.signup.security-question-label')} className="text-secondary mb-3">
                    <Form.Control value={activeSecurityQuestion()}
                                  placeholder="Security question"
                                  disabled={!StringUtils.isEmpty(staticSecurityQuestion)}
                                  onChange={e => setSecurityQuestion(e.target.value)}/>
                </FloatingLabel>

                <FloatingLabel label={t('portal.signup.security-answer-label')} className="text-secondary mb-3">
                    <Form.Control value={securityAnswer} placeholder="Security answer"
                                  onChange={e => setSecurityAnswer(e.target.value)}/>
                </FloatingLabel>
            </>
        )
    }

    const activeSecurityQuestion = () => {
        return (StringUtils.isEmpty(staticSecurityQuestion) ? securityQuestion : staticSecurityQuestion) ?? "";
    }

    const isSignupButtonDisabled = () => {
        return StringUtils.isEmpty(signupValidation.email) ||
                StringUtils.isEmpty(password) ||
                StringUtils.isEmpty(passwordRepeat) ||
                (signupValidation.securityQuestionsEnabled &&
                    (StringUtils.isEmpty(activeSecurityQuestion()) || StringUtils.isEmpty(securityAnswer)));
    }

    return (
        <>
        {t(['portal.signup.header']).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.signup.header"]))} />}
        <div className="w-100 p-4 shadow-sm rounded bg-body">
            <div className="mb-4 text-center">
                <h2>{t("portal.signup.title")}</h2>
            </div>

            {/* show an alert if a validation error occurred */}
            <RenderAlert message={errorMessage}/>

            <FloatingLabel label={t('portal.signup.email-label')} className="text-secondary mb-3">
                <Form.Control type="email" value={signupValidation.email} placeholder="Email"
                              disabled={true}/>
            </FloatingLabel>

            <FloatingLabel label={t('portal.signup.password-label')} className="text-secondary mb-3">
                <Form.Control type="password" value={password} placeholder="Password"
                              onChange={e => setPassword(e.target.value)}/>
            </FloatingLabel>

            <FloatingLabel label={t('portal.signup.password-repeat-label')} className="text-secondary mb-3">
                <Form.Control type="password" value={passwordRepeat} placeholder="Password Repeat"
                              onChange={e => setPasswordRepeat(e.target.value)}/>
            </FloatingLabel>

            {renderSecurityQuestion()}

            {t(['portal.signup.text'])}

            <Button variant="success" className="w-100 mt-4" disabled={isSignupButtonDisabled()}
                    onClick={signup}>
                {t('portal.signup.signup-button')}
            </Button>

            <LocaleSelector className="w-auto mt-4" />

            {t('portal.legal-notice').length > 0 &&
                <div className="mt-2" dangerouslySetInnerHTML={sanitizeHTML(t(["portal.legal-notice"]))} />
            }
        </div>
        {t(['portal.signup.footer']).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t(["portal.signup.footer"]))} />}
        </>
    );
}
